// Marcar filas — la pieza común. Se enchufa en cualquier tabla con tres piezas:
//
//   const marcas = useMarcasFilas('inventario')          // en la página
//   <CabeceraMarca />                                    // primera <th>
//   <FilaMarcable marcas={marcas} clave={k} dark={dark}>  // sustituye a <tr>
//     <MarcaCelda marcas={marcas} clave={k} dark={dark} />
//     …
//   <BarraMarcas dark={dark} marcas={marcas} />          // franja de recuento
//
// La lógica pura (claves y almacén observable) está en `utils/marcasFilas.ts`.
//
// ── Por qué un botón fijo en un canalón y no un hover ni un Alt+clic ─────────
// En estas tablas el clic de la fila ya despliega el acordeón, así que el
// marcado necesita gesto propio. Un control que sólo aparece al pasar el ratón
// no existe para quien no pasa el ratón —ni para el teclado—, y un modificador
// tipo Alt+clic no lo descubre nadie porque nada lo anuncia. Un botón siempre
// presente en su propia columna de 24 px se ve, entra en el orden de tabulación
// y no compite con el clic de la fila. En reposo va en gris de textura, no en
// color: sólo pesa cuando está marcado (PRODUCT.md, principio 1).
//
// ── Por qué fucsia ───────────────────────────────────────────────────────────
// En esta aplicación `sky` = filtro/selección, `emerald` = correcto,
// `amber` = aviso y `red` = problema. Una marca del usuario no es ninguna de las
// cuatro, y pintarla con cualquiera de ellas la haría pasar por un estado del
// dato. Fucsia no aparece en ningún otro sitio del código (medido: cero
// ocurrencias en `src/`), así que sólo puede significar esto. Y como la marca
// nunca depende sólo del color, además del tinte de fondo va el glifo relleno
// en el canalón.
import { useCallback, useMemo, useSyncExternalStore, type ComponentPropsWithoutRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { MarcasFilas } from '@/types'
import { AlmacenMarcas, type AlmacenLike } from '@/utils/marcasFilas'

// Un almacén por ámbito, compartido por todo el que lo pida. Hace falta porque
// las sublíneas se pintan desde otro componente (la fila desplegada) que también
// llama a `useMarcasFilas`: sin registro tendría un almacén propio y las marcas
// del padre y las del hijo no se verían entre sí.
const registro = new Map<string, AlmacenMarcas>()

/** `sessionStorage` puede lanzar sólo con tocarlo (modo privado, cookies
 *  bloqueadas por política). Sin él las marcas siguen funcionando en memoria. */
function almacenNavegador(): AlmacenLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/**
 * Marcas de fila de un ámbito.
 *
 * `ambito` es la clave de persistencia: una por tabla («inventario»,
 * «pedidosCompra», «produccionDiaria»). Devuelve SIEMPRE el mismo objeto, así
 * que la página que lo llama no se re-renderiza nunca por culpa de una marca.
 *
 * Alcance de la persistencia: `sessionStorage`, no `localStorage`. Una marca es
 * una nota de trabajo de la sesión de análisis en curso: tiene que aguantar
 * ordenar, filtrar, paginar e irse al detalle de un pedido y volver, y tiene que
 * desaparecer al cerrar la pestaña. Con `localStorage` el usuario se encontraría
 * el lunes filas marcadas de un análisis de hace tres semanas, sobre datos que
 * ya han cambiado: ruido que además se acumularía tabla a tabla sin que nadie lo
 * limpiase.
 */
export function useMarcasFilas(ambito: string): MarcasFilas {
  return useMemo(() => {
    let almacen = registro.get(ambito)
    if (!almacen) {
      almacen = new AlmacenMarcas(ambito, almacenNavegador())
      registro.set(ambito, almacen)
    }
    return almacen
  }, [ambito])
}

/** ¿Está marcada esta clave? Se suscribe SÓLO a ella: marcar una fila despierta
 *  a esa fila y a nadie más, aunque la tabla tenga miles. */
export function useMarcada(marcas: MarcasFilas, clave: string): boolean {
  const suscribir = useCallback(
    (alCambiar: () => void) => marcas.suscribir(clave, alCambiar),
    [marcas, clave],
  )
  const leer = useCallback(() => marcas.tiene(clave), [marcas, clave])
  return useSyncExternalStore(suscribir, leer, leer)
}

/** Nº de filas marcadas. Se suscribe a lo global, así que sólo re-renderiza al
 *  componente que lo pide (la franja), nunca a la tabla. */
export function useTotalMarcas(marcas: MarcasFilas): number {
  const suscribir = useCallback(
    (alCambiar: () => void) => marcas.suscribir(null, alCambiar),
    [marcas],
  )
  const leer = useCallback(() => marcas.total(), [marcas])
  return useSyncExternalStore(suscribir, leer, leer)
}

// ── Cabecera ──────────────────────────────────────────────────────────────────

/** `<th>` del canalón. Sin rótulo visible —no cabe y no hace falta— pero con
 *  nombre accesible, que es lo que lee un lector de pantalla al tabular.
 *
 *  `relative` NO es decorativo y no se puede quitar. La utilidad `sr-only` de
 *  Tailwind es `position: absolute`, y un absoluto sin ancestro posicionado se
 *  cuelga del bloque contenedor inicial: escapa del `overflow` de TODOS sus
 *  contenedores —incluido el que hace scroll de las filas— y estira el
 *  documento hasta donde caiga. En el Gantt eso se veía como una barra de
 *  scroll de página que aparecía al filtrar (al auto-desplegarse las
 *  coincidencias, cada subtabla añade su cabecera y sus `sr-only` bajan cientos
 *  de píxeles) y dejaba bajar a 2.000 px de pantalla vacía. Con `relative` el
 *  `<th>` es el bloque contenedor y el `sr-only` se queda dentro. */
export function CabeceraMarca({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <th scope="col" className={`relative w-6 px-1 ${className}`}>
      <span className="sr-only">{t('common.marcas.columna')}</span>
    </th>
  )
}

// ── Fila ──────────────────────────────────────────────────────────────────────

type PropsFila = ComponentPropsWithoutRef<'tr'> & {
  marcas: MarcasFilas
  /** Clave estable de la fila. Ver `utils/marcasFilas.claveFila`. */
  clave: string
  dark: boolean
}

/**
 * Sustituye al `<tr>` de la fila. Le añade el tinte cuando está marcada y nada
 * más: el resto de props (className, onClick, style…) pasan tal cual.
 *
 * El tinte se aplica con una clase escrita a mano en `index.css` y no con una
 * utilidad de Tailwind porque las filas ya traen su propio `hover:bg-…`: entre
 * dos utilidades de la misma especificidad gana la que Tailwind escriba después,
 * que no controlamos, y la marca desaparecería al pasar el ratón.
 */
export function FilaMarcable({ marcas, clave, dark, className = '', children, ...resto }: PropsFila) {
  const marcada = useMarcada(marcas, clave)
  return (
    <tr
      {...resto}
      data-marcada={marcada ? '1' : undefined}
      className={`${className} ${marcada ? (dark ? 'fila-marcada-dark' : 'fila-marcada') : ''}`}
    >
      {children}
    </tr>
  )
}

// ── Celda del canalón ─────────────────────────────────────────────────────────

interface PropsCelda {
  marcas: MarcasFilas
  clave: string
  dark: boolean
  /** Subtablas de líneas: menos alto de celda para no desalinear la rejilla. */
  densa?: boolean
  className?: string
}

/** Botón de marcar, dentro de su propio `<td>`. */
export function MarcaCelda({ marcas, clave, dark, densa, className = '' }: PropsCelda) {
  const marcada = useMarcada(marcas, clave)
  return (
    <td className={`w-6 align-middle ${densa ? 'px-1 py-1' : 'px-1 py-2'} ${className}`}>
      <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
    </td>
  )
}

/** Sólo el botón, para tablas que ya tienen una canaleta propia donde meterlo
 *  —reports lleva ahí el expansor y el resumen del bloque— y a las que añadir
 *  una segunda columna les costaría ancho del bloque congelado.
 *
 *  Recibe `marcada` de fuera en vez de suscribirse: quien lo pinta dentro de un
 *  `.map` no puede llamar a un hook por fila. */
export function BotonMarca({ marcas, clave, dark, marcada }: {
  marcas: MarcasFilas
  clave: string
  dark: boolean
  marcada: boolean
}) {
  const { t } = useTranslation()

  const color = marcada
    ? (dark ? 'text-fuchsia-300' : 'text-fuchsia-700')
    : (dark
        ? 'text-slate-600 hover:text-fuchsia-400 hover:bg-fuchsia-500/10'
        : 'text-slate-300 hover:text-fuchsia-500 hover:bg-fuchsia-50')

  return (
    <button
        type="button"
        // La fila de arriba puede tener su propio clic (desplegar el acordeón):
        // el del botón no puede burbujear hasta ella.
        onClick={e => { e.stopPropagation(); marcas.alternar(clave) }}
        aria-pressed={marcada}
        aria-label={marcada ? t('common.marcas.quitar') : t('common.marcas.marcar')}
        title={marcada ? t('common.marcas.quitar') : t('common.marcas.marcar')}
        className={`flex items-center justify-center w-5 h-5 rounded cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 ${color}`}
      >
        {/* Marcador de libro: relleno cuando está marcada, contorno cuando no.
            La forma cambia además del color, para no depender sólo de él. */}
        <svg className="w-3 h-3.5" viewBox="0 0 12 14" aria-hidden="true"
          fill={marcada ? 'currentColor' : 'none'}
          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
          <path d="M2 1.5h8v11l-4-3-4 3v-11z" />
        </svg>
    </button>
  )
}

/** Todas las marcas del ámbito, para pintar sin un hook por fila.
 *
 *  Es la alternativa a `useMarcada` cuando las filas se pintan dentro de un
 *  `.map`: marcar una re-renderiza la tabla entera, así que sólo compensa con
 *  páginas acotadas —reports pagina de 50 en 50—. En listas largas, la pareja
 *  `FilaMarcable` + `MarcaCelda` sigue siendo lo correcto. */
export function useMarcadas(marcas: MarcasFilas): ReadonlySet<string> {
  return useSyncExternalStore(
    useCallback(cb => marcas.suscribir(null, cb), [marcas]),
    () => marcas.todas(),
    () => marcas.todas(),
  )
}

// ── Franja de recuento ────────────────────────────────────────────────────────

/**
 * Cuántas filas hay marcadas y cómo quitarlas todas.
 *
 * Es una franja aparte que sólo existe cuando hay algo que decir, igual que
 * `BarraFiltrosActivos`: metido en la barra de herramientas, el contador la
 * engordaría de forma permanente para un estado que casi siempre está vacío.
 */
export function BarraMarcas({ dark, marcas, className = '' }: {
  dark: boolean
  marcas: MarcasFilas
  className?: string
}) {
  const { t } = useTranslation()
  const total = useTotalMarcas(marcas)
  if (total === 0) return null

  return (
    <div className={`flex items-center gap-2 px-4 py-1.5 border-b ${
      dark
        ? 'border-white/6 bg-fuchsia-500/[0.07]'
        : 'border-slate-100 bg-fuchsia-50/70'
    } ${className}`}>
      <svg className={`w-3 h-3.5 shrink-0 ${dark ? 'text-fuchsia-400' : 'text-fuchsia-500'}`}
        viewBox="0 0 12 14" fill="currentColor" aria-hidden="true">
        <path d="M2 1.5h8v11l-4-3-4 3v-11z" />
      </svg>
      <span className={`text-[11px] font-semibold ${dark ? 'text-fuchsia-200' : 'text-fuchsia-800'}`}>
        {t('common.marcas.total', { count: total })}
      </span>
      <button
        type="button"
        onClick={() => marcas.limpiar()}
        className={`text-[11px] font-medium cursor-pointer transition-colors duration-150 hover:underline ${
          dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
        }`}
      >
        {t('common.marcas.limpiar')}
      </button>
    </div>
  )
}
